/**
 * The KPI cards.
 *
 * Every number is COUNTED from real rows of the SELECTED MONTH — never a placeholder,
 * never derived from a client-side readiness guess, and never filtered by the search
 * box (a search narrows the table, not the month's reality):
 *   Bereit          = rows whose backend preflight returned ready === true
 *   Blockiert       = rows whose backend preflight returned ready === false
 *   Bereits erstellt= ledger rows in status `created`
 *   Fehlgeschlagen  = ledger rows in status `failed`
 *   Zu prüfen       = rows a human has to look at: ledger `creating`/`abandoned`/dirty
 *                     `pending`, a preflight request that errored, and any ledger
 *                     status this frontend cannot translate
 *
 * The last card exists because those three used to be counted nowhere at all — the
 * rows most likely to need attention were the ones the summary hid.
 *
 * "Bereit"/"Blockiert" are only known once a row's preflight has been fetched, so the
 * card shows how many candidates are still unchecked instead of pretending a total it
 * cannot yet know.
 */
import React from "react";

import { ROW_STATUS } from "./bexioDraftLabels";

const CARDS = [
  {
    key: ROW_STATUS.READY,
    title: "Bereit",
    hint: "Entwürfe bereit zur Erstellung",
    icon: "✓",
    tone:
      "text-emerald-600 dark:text-emerald-400 bg-emerald-50 dark:bg-emerald-500/10",
  },
  {
    key: ROW_STATUS.BLOCKED,
    title: "Blockiert",
    hint: "Benötigt Klärung",
    icon: "!",
    tone: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10",
  },
  {
    key: ROW_STATUS.CREATED,
    title: "Bereits erstellt",
    hint: "In Bexio erstellt",
    icon: "▤",
    tone: "text-blue-600 dark:text-blue-400 bg-blue-50 dark:bg-blue-500/10",
  },
  {
    key: ROW_STATUS.FAILED,
    title: "Fehlgeschlagen",
    hint: "Erstellung fehlgeschlagen",
    icon: "⚠",
    tone: "text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-500/10",
  },
  {
    key: "ATTENTION",
    title: "Zu prüfen",
    hint: "Manuelle Prüfung nötig",
    icon: "?",
    tone: "text-amber-600 dark:text-amber-400 bg-amber-50 dark:bg-amber-500/10",
  },
];

export default function BexioDraftSummaryCards({ counts, pendingChecks = 0 }) {
  return (
    <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-5">
      {CARDS.map((card) => {
        const value = counts?.[card.key] ?? 0;
        const showPending =
          pendingChecks > 0 &&
          (card.key === ROW_STATUS.READY || card.key === ROW_STATUS.BLOCKED);

        return (
          <div
            key={card.key}
            className="rounded-xl border border-input bg-card p-4 shadow-sm"
          >
            <div className="flex items-start gap-3">
              <span
                className={`flex h-9 w-9 shrink-0 items-center justify-center rounded-lg text-base ${card.tone}`}
                aria-hidden="true"
              >
                {card.icon}
              </span>
              <div className="min-w-0">
                <div className="text-sm font-medium text-muted-foreground">
                  {card.title}
                </div>
                <div className="mt-0.5 text-2xl font-semibold tabular-nums text-foreground">
                  {value}
                </div>
              </div>
            </div>
            <div className="mt-3 border-t border-input pt-2 text-xs text-muted-foreground">
              {showPending
                ? `${card.hint} · ${pendingChecks} noch nicht geprüft`
                : card.hint}
            </div>
          </div>
        );
      })}
    </div>
  );
}
